import { createContext, useContext, useEffect, useState } from 'react';
import { apiUrl } from '../utils/serverConfig';
import { forgetUnownedDraft } from '../utils/unownedDraft';
import {
  TOKEN_KEY, forgetAdmin, isViewAsToken, parseToken as parseJwt, restoreAdmin, viewAsOf,
} from '../utils/viewAs';

// The stored session, read BEFORE the first render. It used to be restored in
// an effect, which runs after it: PrivateRoute saw no user and redirected to
// /login, and Login did not send a signed-in person back. So every reload,
// deep link and PWA launch landed on the sign-in form with a valid token in
// storage.
export function restoreUser() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;
  const decoded = parseJwt(token);
  if (decoded && decoded.exp * 1000 > Date.now()) return decoded;
  // A view-as token that ran out hands back the admin's own, while that one
  // is still good; otherwise nobody is signed in (utils/viewAs.js).
  if (decoded?.vas) {
    const admin = restoreAdmin();
    return admin ? parseJwt(admin) : null;
  }
  localStorage.removeItem(TOKEN_KEY);
  return null;
}

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(restoreUser);

  // The hour a view-as token lasts ends here as well as on the server: back
  // to the admin's own token, and a fresh page under it.
  const viewEnds = user?.vas ? Number(user.exp) * 1000 : null;
  useEffect(() => {
    if (!viewEnds) return undefined;
    const id = setTimeout(() => {
      restoreAdmin();
      window.location.assign('/');
    }, Math.max(0, viewEnds - Date.now()));
    return () => clearTimeout(id);
  }, [viewEnds]);

  // Another tab signed in, out, or began or left viewing as a role. Entering
  // or leaving a view reloads this tab, so nothing it shows or holds was read
  // under the other identity; anything else is just followed.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== TOKEN_KEY && event.key !== null) return;
      if (event.key === TOKEN_KEY && (isViewAsToken(event.oldValue) || isViewAsToken(event.newValue))) {
        window.location.reload();
        return;
      }
      setUser(restoreUser());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = (token) => {
    forgetAdmin();
    localStorage.setItem(TOKEN_KEY, token);
    setUser(parseJwt(token));
  };

  const logout = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // apiUrl(), not the raw constant: an unset REACT_APP_API_URL made this
      // request the literal string "undefined/auth/logout". Plain fetch rather
      // than the axios instance is deliberate -- logout must not be caught by
      // the 401 interceptor and turned into a redirect loop.
      fetch(apiUrl('/auth/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    // Both tokens: signing out while viewing as a role must not leave the
    // admin's own behind on this device.
    localStorage.removeItem(TOKEN_KEY);
    forgetAdmin();
    forgetUnownedDraft();
    setUser(null);
    window.location.href = window.location.pathname.startsWith('/survey') ? '/survey' : '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, viewAs: viewAsOf(user) }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}